'use client';

import { useId, useRef, useState, type CSSProperties } from 'react';
import * as Ariakit from '@ariakit/react';
import { SField, type SFieldSize, type SFieldAddonAlign, type SFieldStatus } from '../SField';
import { SIcon, type SIconName } from '../SIcon';
import { type SColor } from '../../lib/color';
import { SGhostButton } from '../SGhostButton';
import { SChip } from '../SChip';
import { cn } from '../../lib/cn';
import { usePortalContainer } from '../../lib/portal-container';
import { runRules, useFormField, type Rule } from '../../lib/form';
import { type STooltipProps } from '../STooltip';

export type SFilePickerValue = File[] | File | null;
export type SFilePickerRejectReason = 'max-file-size' | 'max-total-size' | 'max-files';

export interface SFilePickerProps {
  value?: SFilePickerValue;
  /** 파일 변경 (sdUpdate) */
  onValueChange?: (value: SFilePickerValue) => void;
  /** 제한 초과 거부 (sdReject) */
  onReject?: (detail: { files: File[]; reason: SFilePickerRejectReason }) => void;
  placeholder?: string;
  disabled?: boolean;
  /** 포커스 상태 여부 */
  focused?: boolean;
  /** 호버 상태 여부 */
  hovered?: boolean;
  /** 인라인 표시 — SField 테두리 박스만 사라지고 label/rules/hint 는 그대로 동작 */
  inline?: boolean;
  multiple?: boolean;
  /** multiple 파일 목록을 필드 아래 칩 대신, caret(▼) 클릭 시 열리는 리스트박스 카드로 표시 (바깥 클릭 시 닫힘) */
  useMultipleListBox?: boolean;
  accept?: string;
  maxFileSize?: number | string;
  maxTotalSize?: number | string;
  maxFiles?: number | string;
  name?: string;
  rules?: Rule[];
  status?: SFieldStatus;
  size?: SFieldSize;
  // ── SField ──
  label?: string;
  labelWidth?: number | string;
  icon?: SIconName;
  iconColor?: SColor;
  labelTooltip?: string;
  labelTooltipProps?: Partial<STooltipProps>;
  addonLabel?: string;
  addonAlign?: SFieldAddonAlign;
  hint?: string;
  error?: boolean;
  errorMessage?: string;
  width?: number | string;
  className?: string;
  style?: CSSProperties;
}

const toBytes = (v?: number | string) =>
  v == null ? undefined : typeof v === 'string' ? Number(v) : v;

/** SFilePicker — sd-file-picker 포팅. 단일/다중 파일 + 크기·개수 검증 + 칩 목록. */
export function SFilePicker({
  value = null,
  onValueChange,
  onReject,
  placeholder = 'Click to upload',
  disabled = false,
  focused = false,
  hovered = false,
  inline = false,
  multiple = false,
  useMultipleListBox = false,
  accept,
  maxFileSize,
  maxTotalSize,
  maxFiles,
  name,
  rules,
  status,
  size = 'sm',
  label,
  labelWidth,
  icon,
  iconColor,
  labelTooltip,
  labelTooltipProps,
  addonLabel,
  addonAlign = 'start',
  hint,
  error,
  errorMessage,
  width,
  className,
  style,
}: SFilePickerProps) {
  const autoId = useId();
  const resolvedName = name ?? autoId;
  const inputRef = useRef<HTMLInputElement>(null);
  const contentBoxRef = useRef<HTMLDivElement>(null);
  const [ruleError, setRuleError] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const portalContainer = usePortalContainer();
  const files = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value && !Array.isArray(value)
      ? value
      : null;
  const hasFiles = multiple ? (files as File[]).length > 0 : !!files;
  const displayText = !hasFiles
    ? placeholder
    : multiple
      ? (files as File[]).map(f => f.name).join(', ')
      : (files as File).name;

  // SForm 등록 — submit 시 폼이 이 필드를 일괄 검증한다 (폼 밖이면 no-op)
  useFormField({
    name,
    validate: () => {
      const msg = runRules(files, rules);
      setRuleError(msg);
      return msg === '';
    },
    resetValidation: () => setRuleError(''),
  });

  const validate = (
    arr: File[],
    existing = 0,
  ): { accepted: File[]; rejected: File[]; reason?: SFilePickerRejectReason } => {
    const mfs = toBytes(maxFileSize),
      mts = toBytes(maxTotalSize),
      mf = maxFiles != null ? Number(maxFiles) : undefined;
    if (mf != null && existing + arr.length > mf)
      return { accepted: [], rejected: arr, reason: 'max-files' };
    if (mfs != null) {
      const over = arr.filter(f => f.size > mfs);
      if (over.length) return { accepted: [], rejected: over, reason: 'max-file-size' };
    }
    if (mts != null && arr.reduce((s, f) => s + f.size, 0) > mts)
      return { accepted: [], rejected: arr, reason: 'max-total-size' };
    return { accepted: arr, rejected: [] };
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list || list.length === 0) return;
    const arr = Array.from(list);
    if (inputRef.current) inputRef.current.value = '';
    if (multiple) {
      const existing = Array.isArray(value) ? value : [];
      const { accepted, rejected, reason } = validate(arr, existing.length);
      if (reason) {
        onReject?.({ files: rejected, reason });
        return;
      }
      const merged = [...existing, ...accepted];
      onValueChange?.(merged);
      if (rules?.length) setRuleError(runRules(merged, rules));
    } else {
      const { accepted, rejected, reason } = validate(arr);
      if (reason) {
        onReject?.({ files: rejected, reason });
        return;
      }
      onValueChange?.(accepted[0]);
      if (rules?.length) setRuleError(runRules(accepted[0], rules));
    }
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onValueChange?.(multiple ? [] : null);
    setListOpen(false);
  };
  const removeChip = (i: number) => {
    const next = (files as File[]).filter((_, idx) => idx !== i);
    onValueChange?.(next);
    // 리스트박스에서 마지막 파일을 지우면 caret 이 사라지므로 함께 닫는다.
    if (next.length === 0) setListOpen(false);
  };
  const open = () => {
    if (!disabled) inputRef.current?.click();
  };

  const resolvedError = error || ruleError !== '';
  const resolvedStatus =
    status ?? (rules?.length ? (ruleError !== '' ? 'error' : undefined) : undefined);
  const padX =
    size === 'md' ? 'var(--cmp-filepicker-md-paddingX)' : 'var(--cmp-filepicker-sm-paddingX)';
  const iconSize = size === 'md' ? 20 : 16;

  const gapCls =
    size === 'md' ? 'gap-[var(--cmp-filepicker-md-gap)]' : 'gap-[var(--cmp-filepicker-sm-gap)]';

  // inline 모드도 SField 를 그대로 사용한다(label/rules/hint/errorMessage 동작 동일).
  // 차이는 테두리 박스(border/배경/패딩)가 사라지는 것뿐이고,
  // placeholder 텍스트는 hover 시 기본 텍스트 색으로 진해진다.
  const inlinePlaceholder = inline && !disabled && !hasFiles;

  const showListbox = multiple && useMultipleListBox;
  const filesArr = multiple ? (files as File[]) : [];

  // 열기 트리거는 실제 <button> 으로 두고, clear/caret 버튼은 형제로 분리한다.
  // (role="button" 안에 SGhostButton(<button>) 을 넣던 상호작용 요소 중첩 제거.)
  const contentInner = (
    <div
      ref={contentBoxRef}
      className={cn('group/filepicker flex h-full w-full items-center', gapCls)}
      style={{ padding: inline ? 0 : `0 ${padX}` }}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        disabled={disabled}
        multiple={multiple}
        accept={accept}
        name={resolvedName}
        onChange={onChange}
        aria-label={placeholder}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={open}
        aria-label={placeholder}
        className={cn(
          'flex min-w-0 flex-1 items-center bg-transparent text-left',
          gapCls,
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <SIcon
          name="attachFile"
          size={iconSize}
          color={disabled ? 'var(--cmp-filepicker-icon-disabled)' : 'var(--color-grey-65)'}
        />
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-[12px]',
            inlinePlaceholder &&
              'text-[color:var(--cmp-filepicker-text-placeholder)] transition-colors group-hover/filepicker:text-[color:var(--cmp-filepicker-text-default)]',
          )}
          style={
            inlinePlaceholder
              ? undefined
              : {
                  color: disabled
                    ? 'var(--cmp-filepicker-text-disabled)'
                    : hasFiles
                      ? 'var(--cmp-filepicker-text-default)'
                      : 'var(--cmp-filepicker-text-placeholder)',
                }
          }
        >
          {displayText}
        </span>
      </button>
      {/* useMultipleListBox: caret(▼) 클릭 시 파일 목록 리스트박스 토글 */}
      {showListbox && hasFiles && !disabled && (
        <Ariakit.PopoverDisclosure
          render={<SGhostButton icon="caretDown" size="xxs" ariaLabel="toggle file list" />}
          className={cn('shrink-0 transition-transform duration-150', listOpen && 'rotate-180')}
        />
      )}
      {!disabled && hasFiles && (
        <SGhostButton icon="close" size="xxs" ariaLabel="clear" onClick={clear} />
      )}
    </div>
  );

  // useMultipleListBox: caret 클릭 시 열리는 파일 목록 카드.
  // sd-select-listbox 와 동일한 배경/radius/그림자, 칩을 세로로 나열하며 바깥 클릭 시 닫힌다.
  // 위치·너비는 caret(disclosure) 이 아니라 SField 입력 박스(contentBoxRef)에 맞춘다.
  // - getAnchorRect: 입력 박스 좌측 하단에 정렬 (caret 아래가 아님)
  // - minWidth: 최소 입력 박스 너비 (긴 파일명은 그 이상으로 늘어날 수 있음)
  const content = showListbox ? (
    <Ariakit.PopoverProvider open={listOpen} setOpen={setListOpen} placement="bottom-start">
      {contentInner}
      <Ariakit.Popover
        portal
        portalElement={portalContainer}
        gutter={4}
        unmountOnHide
        getAnchorRect={() => contentBoxRef.current?.getBoundingClientRect() ?? null}
        style={{ minWidth: contentBoxRef.current?.offsetWidth }}
        className="z-50 box-border flex flex-col items-start gap-[8px] rounded-[var(--cmp-listBox-radius)] bg-[var(--cmp-listBox-bg)] p-[8px] shadow-[2px_2px_12px_2px_rgba(0,0,0,0.1)]"
      >
        {filesArr.map((f, i) => (
          <SChip key={i} value={f.name} editable={false} onRemove={() => removeChip(i)} />
        ))}
      </Ariakit.Popover>
    </Ariakit.PopoverProvider>
  ) : (
    contentInner
  );

  // 기본(useMultipleListBox=false) multiple: 칩을 필드 아래 flex-wrap 으로 가로 나열.
  const chips = multiple && !useMultipleListBox && filesArr.length > 0 && (
    <div className="mt-[8px] flex flex-wrap gap-[8px]">
      {filesArr.map((f, i) => (
        <SChip key={i} value={f.name} editable={false} onRemove={() => removeChip(i)} />
      ))}
    </div>
  );

  return (
    <div className={className} style={style}>
      <SField
        borderless={inline}
        label={label}
        labelWidth={labelWidth}
        icon={icon}
        iconColor={iconColor}
        labelTooltip={labelTooltip}
        labelTooltipProps={labelTooltipProps}
        addonLabel={addonLabel}
        addonAlign={addonAlign}
        size={size}
        hint={hint}
        error={resolvedError}
        status={resolvedStatus}
        focused={focused}
        hovered={hovered}
        errorMessage={ruleError !== '' ? ruleError : errorMessage}
        width={width}
        disabled={disabled}
      >
        {content}
      </SField>
      {chips}
    </div>
  );
}
